import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Phase } from '@erm/api-client';
import { Icon, type IconName } from '../icons/index.js';

/**
 * The system components — DESIGN_SYSTEM.md §4 and §8.
 *
 * The SAME seven pieces as the native app's `components/system.tsx`, with the
 * same names, the same rules and the same vocabulary. That is the point: a
 * screen laid out from `PageHeader · Primary · Section · ObjectCard · StatRow ·
 * QuickActions` reads as the same product whichever platform it is on, because
 * the composition is the same composition and not two people's idea of one.
 *
 * The rule they enforce:
 *
 *   **Emphasis is a budget, not a property.** One screen gets ONE dominant
 *   element. `Primary` is that element, there is at most one of it, and it is
 *   the only thing on a screen allowed the accent rule.
 *
 * Two things are deliberately impossible here:
 *
 *   · **A section cannot be a box.** `Section` is a title, twelve pixels, and
 *     its content on the page ground.
 *   · **An icon cannot have a coloured plate.** There is no tone prop on any
 *     drawing in this file. Semantic colour marks a STATE, on a word or a rule
 *     — never a decorative ground behind a glyph.
 */

/* ---- page header --------------------------------------------------------- */

export function PageHeader({
  title,
  context,
  eyebrow,
  trailing,
}: {
  title: string;
  /** One line, and only when it says something the title does not. */
  context?: string;
  /** Whose surface this is — "Owner · Fleet workspace". Sentence case. */
  eyebrow?: { icon: IconName; text: string };
  trailing?: ReactNode;
}): ReactElement {
  return (
    <header className="page-head">
      {eyebrow === undefined ? null : (
        <p className="page-head__eyebrow">
          <Icon name={eyebrow.icon} size="sm" />
          {eyebrow.text}
        </p>
      )}
      <div className="page-head__row">
        <h1 className="page-head__title">{title}</h1>
        {trailing === undefined ? null : <div className="page-head__trailing">{trailing}</div>}
      </div>
      {context === undefined ? null : <p className="page-head__context">{context}</p>}
    </header>
  );
}

/* ---- the one dominant element -------------------------------------------- */

/**
 * The one thing to do next.
 *
 * At most ONE per screen, and the only element allowed the accent leading rule.
 * That constraint is the whole design: an owner who opens the product and sees
 * a single orange-edged line saying *"3 machines are not published"* knows what
 * to do; an owner who sees a hero card, a proportion bar, an amber panel and
 * four tiles knows only that the app has a lot to say.
 *
 * Deliberately **not a card**. It sits on the page ground with a rule down its
 * leading edge, so it reads as the page speaking rather than as another box in
 * a stack of boxes — a box would put it in the same visual class as the machine
 * cards beneath it, which is exactly the equivalence being avoided.
 *
 * When nothing needs doing a screen renders NOTHING here. An "all clear" panel
 * dressed as an action spends the whole emphasis budget on the absence of news,
 * and within a week the rule stops meaning anything.
 */
export function Primary({
  title,
  body,
  icon,
  action,
}: {
  title: string;
  body?: string;
  icon?: IconName;
  action?: { label: string; to: string } | { label: string; onClick: () => void };
}): ReactElement {
  const cta =
    action === undefined ? null : (
      <>
        {action.label}
        <Icon name="chevron" size="sm" />
      </>
    );

  return (
    <section className="primary enter">
      <div className="primary__body">
        <h2 className="primary__title">
          {icon === undefined ? null : <Icon name={icon} size="sm" />}
          {title}
        </h2>
        {body === undefined ? null : <p className="primary__text">{body}</p>}
        {action === undefined ? null : 'to' in action ? (
          <Link className="primary__cta interactive" to={action.to}>
            {cta}
          </Link>
        ) : (
          <button type="button" className="primary__cta interactive" onClick={action.onClick}>
            {cta}
          </button>
        )}
      </div>
    </section>
  );
}

/* ---- section ------------------------------------------------------------- */

/**
 * A group of content under a name. NOT a box — §4.1.
 *
 * Boxing sections is what turned a three-machine fleet into eleven containers,
 * and unboxing them is the single change that most separates a designed screen
 * from an assembled one: the boxes were doing work that spacing should do.
 *
 * The title is `h2`-sized but visually quieter than the page title, because a
 * section heading that competes with the page title leaves the screen with no
 * heading at all.
 */
export function Section({
  title,
  action,
  children,
}: {
  title: string;
  /** One quiet link out. Never a button. */
  action?: { label: string; to: string };
  children: ReactNode;
}): ReactElement {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">{title}</h2>
        {action === undefined ? null : (
          <Link className="section__action" to={action.to}>
            {action.label}
            <Icon name="chevron" size="sm" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

/* ---- objects ------------------------------------------------------------- */

export type ObjectState = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | null;

/**
 * A card, and the only kind of card there is.
 *
 * A card is an object a person could point at: a machine, an offer, a booking,
 * an upload, a transport leg. It is never a section, a number, a heading, a
 * paragraph, a legend or a note.
 *
 * `state` draws a hairline rule down the leading edge in the semantic colour.
 * That is one of the four permitted uses of semantic colour, and it is
 * permitted because the colour is a fact ABOUT THIS OBJECT rather than
 * decoration applied to it. The state's WORD always appears in the card too;
 * the rule is a second signal, never the only one.
 */
export function ObjectCard({
  children,
  to,
  state = null,
  label,
  className: extra,
}: {
  children: ReactNode;
  to?: string;
  state?: ObjectState;
  /** The accessible name of the whole card, when it is a link. */
  label?: string;
  /**
   * One page-scoped hook, for what is specific to THIS kind of object.
   *
   * Not an escape hatch for restyling the card: the surface, the border, the
   * radius and the state rule all stay with the component. It exists so a
   * meter reading can size its own figure without the page re-implementing a
   * card to do it.
   */
  className?: string;
}): ReactElement {
  const className = `object${state === null ? '' : ` object--${state}`}${
    extra === undefined ? '' : ` ${extra}`
  }`;
  if (to === undefined) return <article className={className}>{children}</article>;
  return (
    <Link
      className={`${className} object--link liftable`}
      to={to}
      {...(label === undefined ? {} : { 'aria-label': label })}
    >
      {children}
    </Link>
  );
}

/* ---- facts --------------------------------------------------------------- */

export interface Stat {
  value: number | string;
  label: string;
  /** A state this figure is ABOUT. Never for decoration. */
  state?: Exclude<ObjectState, null>;
}

/**
 * Counts, as typography rather than as tiles.
 *
 * This replaced four cards, four coloured icon plates and four progress rails —
 * for four numbers whose largest value was three. Setting them close together
 * is what makes them comparable, which is the only thing a reader wanted from
 * them.
 *
 * There is no bar, no ring and no chart. Nothing here is a percentage: every
 * figure is a count of rows the API returned, and a reader can check the
 * arithmetic by adding them up.
 */
export function StatRow({
  stats,
  hideZero = false,
}: {
  stats: readonly Stat[];
  /**
   * Off by default, because a zero is often the POINT — "0 published" against
   * "3 machines" is the reason to act, and hiding it hides the finding.
   */
  hideZero?: boolean;
}): ReactElement | null {
  const shown = hideZero ? stats.filter((s) => s.value !== 0 && s.value !== '0') : stats;
  if (shown.length === 0) return null;
  return (
    <dl className="statrow">
      {shown.map((stat) => (
        <div key={stat.label} className="statrow__item">
          <dt className="statrow__label">{stat.label}</dt>
          <dd
            className={`statrow__value${stat.state === undefined ? '' : ` statrow__value--${stat.state}`}`}
          >
            {stat.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A state, said in a word with a dot beside it.
 *
 * The dot is one of the four permitted uses of semantic colour, and only
 * because the word is immediately next to it. A row of coloured dots with the
 * words in a legend somewhere else is the pattern this replaces.
 */
export function StatusLine({
  state,
  children,
}: {
  state: Exclude<ObjectState, null>;
  children: ReactNode;
}): ReactElement {
  return (
    <p className={`statusline statusline--${state}`}>
      <span className="statusline__dot" aria-hidden="true" />
      {children}
    </p>
  );
}

/* ---- operational phase --------------------------------------------------- */

/**
 * Where this operation has got to.
 *
 * The SAME component as the native app's, with the same shape and the same
 * rules. `planned → scheduled → dispatched → in_transit → arrived` is a
 * sequence and used to render as a single chip; a person could not see what had
 * already happened or what came next without knowing the state machine.
 *
 * Horizontal, because an operational sequence is short and its SHAPE is the
 * information: how far along, and how much is left.
 *
 * The chip alongside it stays. §13.1's rule holds here as everywhere — the rail
 * is a reading of the machine's state, offered, never substituted for it.
 */
export function PhaseRail({
  phases,
  offSequence = false,
  note,
}: {
  phases: readonly Phase[];
  offSequence?: boolean;
  note?: string;
}): ReactElement {
  return (
    <div className="phases">
      <ol className="phases__track">
        {phases.map((phase) => (
          <li key={phase.key} className={`phase phase--${phase.status}`}>
            <span className="phase__mark" aria-hidden="true">
              <Icon
                name={
                  phase.status === 'done'
                    ? 'stepDone'
                    : phase.status === 'current'
                      ? 'stepCurrent'
                      : 'stepUpcoming'
                }
                size="sm"
              />
            </span>
            <span className="phase__label">{phase.label}</span>
            <span className="visually-hidden">
              {phase.status === 'done'
                ? 'done'
                : phase.status === 'current'
                  ? 'now'
                  : 'not started'}
            </span>
          </li>
        ))}
      </ol>
      {offSequence ? (
        <p className="phases__note phases__note--off">
          {note ?? 'This leg is not on the usual sequence right now.'}
        </p>
      ) : note === undefined ? null : (
        <p className="phases__note">{note}</p>
      )}
    </div>
  );
}

/* ---- quiet actions ------------------------------------------------------- */

export interface QuickActionItem {
  icon: IconName;
  label: string;
  to: string;
  /**
   * How many things are waiting behind this action.
   *
   * A numeral beside the word, not a coloured badge dot. The count is what
   * makes the action worth pressing, so it stays; the red circle was
   * decoration, and it went.
   */
  count?: number;
}

export function QuickActions({ actions }: { actions: readonly QuickActionItem[] }): ReactElement {
  return (
    <nav className="quick" aria-label="Quick actions">
      {actions.map((action) => (
        <Link
          key={action.label}
          className="quick__item interactive"
          to={action.to}
          aria-label={
            action.count !== undefined && action.count > 0
              ? `${action.label}, ${action.count}`
              : action.label
          }
        >
          <Icon name={action.icon} size="md" />
          <span className="quick__label">
            {action.label}
            {action.count !== undefined && action.count > 0 ? (
              <span className="quick__count">{action.count}</span>
            ) : null}
          </span>
        </Link>
      ))}
    </nav>
  );
}

/** A hairline, for where content would otherwise run together. */
export function Divider(): ReactElement {
  return <hr className="divider" />;
}

/* ==========================================================================
 * Facts — label/value pairs, unboxed
 * ========================================================================== */

export interface Fact {
  label: string;
  value: ReactNode;
  /** Tabular monospace: money, meter readings, identifiers, timestamps. */
  numeric?: boolean;
  /** A state this fact is ABOUT. Never decoration. */
  state?: Exclude<ObjectState, null>;
}

/**
 * The facts about a thing, as type.
 *
 * This is what `DefinitionList` should always have been: no box, no rules
 * between rows, no alternating ground. A `<dl>` laid out so the labels form a
 * column a reader can scan and the values form a column a reader can compare —
 * which is the only reason to put facts in a list rather than a sentence.
 *
 * `columns` is a MAXIMUM, not a promise: the grid collapses to one column on a
 * phone and to two on a tablet regardless, because three 15px values across a
 * 360px screen is three columns of one word each.
 */
export function Facts({
  facts,
  columns = 2,
  label,
}: {
  facts: readonly Fact[];
  columns?: 1 | 2 | 3;
  /** Announced when the list has no visible heading of its own. */
  label?: string;
}): ReactElement | null {
  if (facts.length === 0) return null;
  return (
    <dl
      className={`facts facts--${String(columns)}`}
      {...(label === undefined ? {} : { 'aria-label': label })}
    >
      {facts.map((fact) => (
        <div key={fact.label} className="facts__item">
          <dt className="facts__label">{fact.label}</dt>
          <dd
            className={[
              'facts__value',
              fact.numeric === true ? 'numeric' : '',
              fact.state === undefined ? '' : `facts__value--${fact.state}`,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ==========================================================================
 * Money
 * ========================================================================== */

/**
 * An amount, typeset as the subject.
 *
 * Nine screens in this product have money as their subject and none of them
 * SET money as their subject — the figure a person is being asked to commit to
 * renders at the same 14px as the row label beside it. This gives it size, the
 * monospace face and tabular figures, and puts the currency where it belongs:
 * beside the number, quieter than it, never in front of it at the same weight.
 *
 * `incomplete` is a first-class state rather than a footnote, because BR-23.4
 * makes "incomplete but proceeding" legitimate: a total that is not yet final
 * must LOOK not-yet-final, or the product has quietly claimed something the
 * backend refused to.
 */
export function Money({
  amountMinor,
  currency,
  format,
  size = 'md',
  label,
  incomplete = false,
  note,
}: {
  /**
   * MINOR units, as the string the API sent.
   *
   * A string, never a number, and never parsed on the way in: an amount in
   * paise can exceed `Number.MAX_SAFE_INTEGER` and money that silently loses
   * its last digits is worse than money that fails to render. (A `BigInt` fed
   * to `Intl.NumberFormat` is also what took down the equipment detail screen
   * on every iPhone while every browser rendered it — Hermes refuses it.)
   */
  amountMinor: string;
  currency: string;
  /** The shared `formatMinorUnits` — passed in so this file stays presentational. */
  format: (amount: string, currency: string) => string;
  size?: 'sm' | 'md' | 'lg';
  /** What this amount is. Sits above the figure, quieter. */
  label?: string;
  /** The figure is a partial total — say so, visibly. */
  incomplete?: boolean;
  /** One line under the figure: a basis, an as-of, a caveat. */
  note?: string;
}): ReactElement {
  return (
    <div className={`money money--${size}`}>
      {label === undefined ? null : <p className="money__label">{label}</p>}
      <p className="money__figure">
        <span className="money__currency">{currency}</span>
        <span className="money__amount numeric">{format(amountMinor, currency)}</span>
        {incomplete ? (
          <span className="money__incomplete">
            <Icon name="attention" size="sm" />
            not final
          </span>
        ) : null}
      </p>
      {note === undefined ? null : <p className="money__note">{note}</p>}
    </div>
  );
}

/* ==========================================================================
 * Note — a quiet aside
 * ========================================================================== */

export type NoteTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/**
 * One thing worth saying that is not the content.
 *
 * This replaces nine hand-rolled variants (`owner-note`, `placeholder-note`,
 * `estimate__note`, `snapshot__provenance`, and six more), several of which had
 * drifted into filling a semantic-subtle panel — the exact pattern §1.2
 * forbids, and the pattern that costs `warning` its meaning by the third time a
 * reader sees an amber box that was not a warning.
 *
 * The treatment is a leading rule and coloured type, which are two of the four
 * permitted shapes for semantic colour. There is no ground, no border box and
 * no icon plate.
 */
export function Note({
  tone = 'neutral',
  icon,
  children,
  role,
}: {
  tone?: NoteTone;
  icon?: IconName;
  children: ReactNode;
  /** `alert` for something that just went wrong; `status` for a live condition. */
  role?: 'alert' | 'status';
}): ReactElement {
  return (
    <p className={`note note--${tone}`} {...(role === undefined ? {} : { role })}>
      {icon === undefined ? null : <Icon name={icon} size="sm" />}
      <span>{children}</span>
    </p>
  );
}

/* ==========================================================================
 * ActionBar
 * ========================================================================== */

export interface ActionBarProps {
  /** The one control the screen is asking for. Rendered heaviest. */
  children: ReactNode;
  /**
   * Pin to the bottom of the viewport below `md`.
   *
   * Uses `position: sticky` rather than `fixed` deliberately: the shell wraps
   * every route in an element with a transform entrance, and a transformed
   * ancestor is a containing block for fixed descendants — which is how a
   * previous action bar anchored itself three thousand pixels down the page.
   */
  sticky?: boolean;
  /** One line above the controls: what pressing the primary will do. */
  note?: string;
}

/**
 * The decisions available on this screen, in one place, with one of them
 * heaviest.
 *
 * "One strong CTA over five equal buttons" is not a stylistic preference here:
 * this product's actions are irreversible (accept freezes terms, publish makes
 * a rate card immutable, cancel starts a fee calculation), and a row of equally
 * weighted buttons is how somebody presses the wrong one.
 */
export function ActionBar({ children, sticky = false, note }: ActionBarProps): ReactElement {
  return (
    <div className={`actionbar${sticky ? ' actionbar--sticky' : ''}`}>
      {note === undefined ? null : <p className="actionbar__note">{note}</p>}
      <div className="actionbar__controls">{children}</div>
    </div>
  );
}

/* ==========================================================================
 * Record — the chronology
 * ========================================================================== */

export interface RecordEntry {
  id: string;
  /** ISO instant. Rendered as a `<time>` so the machine-readable value survives. */
  at: string;
  title: ReactNode;
  detail?: ReactNode;
  /** Which machine, leg or channel this belongs to. */
  channel?: string;
  state?: Exclude<ObjectState, null>;
}

/**
 * What happened, in order.
 *
 * The order IS the meaning, so it is an `<ol>`, and the connecting rule is
 * drawn in CSS and hidden — list semantics already convey sequence and a second
 * announcement of it is noise on a screen reader.
 *
 * This is the product's most under-used asset. The whole positioning is "the
 * record of the machine"; the record was rendering as a caption list.
 */
export function Record({
  entries,
  label,
}: {
  entries: readonly RecordEntry[];
  label: string;
}): ReactElement {
  return (
    <ol className="record" aria-label={label}>
      {entries.map((entry) => (
        <li
          key={entry.id}
          className={`record__item${entry.state === undefined ? '' : ` record__item--${entry.state}`}`}
        >
          <span className="record__mark" aria-hidden="true" />
          <div className="record__body">
            <p className="record__title">{entry.title}</p>
            {entry.detail === undefined ? null : <p className="record__detail">{entry.detail}</p>}
            <p className="record__meta">
              <time className="numeric" dateTime={entry.at}>
                {new Date(entry.at).toLocaleString()}
              </time>
              {entry.channel === undefined ? null : (
                <span className="record__channel">{entry.channel}</span>
              )}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ==========================================================================
 * Seat — whose half of the product this is
 * ========================================================================== */

export type Seat = 'customer' | 'owner' | 'support';

const SEAT: Record<Seat, { icon: IconName; word: string }> = {
  customer: { icon: 'bookings', word: 'Renting' },
  owner: { icon: 'fleet', word: 'Owner' },
  support: { icon: 'trusted', word: 'Support' },
};

/**
 * The role signal — audit finding C3.
 *
 * Customer, owner and support chrome were pixel-identical, and the only thing
 * telling an owner they were in the owner half of the product was the words in
 * a sidebar. This is the smallest honest fix: one word and one drawing, in the
 * topbar beside the mark, plus the sunken page ground the owner and support
 * shells now sit on.
 *
 * Deliberately NOT a coloured chip. A second brand colour per role would give
 * this product three brands, and the seat is not a status.
 */
export function SeatBadge({ seat }: { seat: Seat }): ReactElement {
  const { icon, word } = SEAT[seat];
  return (
    <span className={`seat seat--${seat}`}>
      <Icon name={icon} size="sm" />
      {word}
    </span>
  );
}

/* ==========================================================================
 * ContextRail — the ≥1440 column
 * ========================================================================== */

/**
 * The thing a reader keeps glancing back at while they read everything else.
 *
 * On a wide screen this product had a measure problem rather than a space
 * problem: `.page` caps at 1120px and everything past it was margin. A generic
 * dashboard sidebar would have been the wrong answer — this rail is not chrome
 * and it is not navigation. It holds the ONE piece of standing context the rest
 * of the screen is about: where a booking stands while you read its record,
 * which phase an operation is in while you fill in its form.
 *
 * Three rules keep it honest:
 *
 *   · **It duplicates nothing.** The content MOVES here at `xxl`; it is not a
 *     second copy of something still in the main column.
 *   · **It holds no actions.** A decision that is only reachable at 1440px is a
 *     decision most people cannot reach.
 *   · **Below `xxl` it flows back inline, in reading order**, because it is a
 *     real DOM child in the right place rather than a portal.
 */
export function ContextRail({
  children,
  label = 'Context',
}: {
  children: ReactNode;
  /** The landmark's accessible name — it is an `aside`, so it needs one. */
  label?: string;
}): ReactElement {
  return (
    <aside className="context-rail" aria-label={label}>
      {children}
    </aside>
  );
}
